import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
    User,
    X,
    ChevronDown,
    ChevronRight,
    Menu,
    Globe,
    Mail,
    Phone,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import GoogleTranslate from "../../google-translate/GoogleTranslate";
import { courseApi, noticeApi } from "../../api/api";
import api from "../../utils/api";
import LanguagePopup from "../LanguagePopup";
import { useAuth } from "../../utils/AuthContext";

const Navbar = () => {
    const [showLangPopup, setShowLangPopup] = useState(false);
    const [pages, setPages] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [isPopupOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const { user, logout } = useAuth();

    const navigate = useNavigate();
    const dropdownRef = useRef(null);
    const [loading, setLoading] = useState(true);

    const marqueeContainerRef = useRef(null);
    const marqueeTextRef = useRef(null);
    const animationFrameRef = useRef(null);
    const scrollPositionRef = useRef(0);
    const [offers, setOffers] = useState([]);

    // Handle Scroll for Smart Sticky Nav
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        api.get(`${noticeApi}`)
            .then((res) => {
                if (res.data?.success && res.data?.data?.length > 0) {
                    setOffers(res.data.data);
                }
            })
            .catch((err) => console.error("Offer API Error:", err))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const fetchPages = async () => {
            try {
                const res = await api.get(`${courseApi}`);
                const data = res.data;
                const uniquePages = [];
                data.forEach((course) => {
                    if (!uniquePages.some((p) => p.name === course.name)) {
                        uniquePages.push({
                            name: course.name,
                            id: course.id,
                            parent_id: course.parent_id,
                        });
                    }
                });
                setPages(uniquePages);
            } catch (err) {
                console.error("Error fetching pages:", err);
            }
        };
        fetchPages();
    }, []);

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!isMobileMenuOpen);
        setDropdownOpen(false);
        setOpenDropdown(null);
    };

    const toggleGroupedDropdown = (menuName) => {
        setOpenDropdown(openDropdown === menuName ? null : menuName);
    };

    const handleLogout = () => {
        logout();
        setDropdownOpen(false);
        navigate("/login");
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target)
            ) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setIsLoggedIn(Boolean(user?.token));
    }, [user]);

    // Marquee logic
    useEffect(() => {
        if (isPopupOpen) {
            if (animationFrameRef.current)
                cancelAnimationFrame(animationFrameRef.current);
            return;
        }
        const container = marqueeContainerRef.current;
        const text = marqueeTextRef.current;
        if (!container || !text) return;

        const containerWidth = container.offsetWidth;
        const textWidth = text.offsetWidth;
        let pos = scrollPositionRef.current;
        const speedSlow = 0.5;

        const step = () => {
            pos -= speedSlow;
            if (pos <= -textWidth) pos = containerWidth;
            scrollPositionRef.current = pos;
            text.style.transform = `translateX(${pos}px)`;
            animationFrameRef.current = requestAnimationFrame(step);
        };

        animationFrameRef.current = requestAnimationFrame(step);
        return () => {
            if (animationFrameRef.current)
                cancelAnimationFrame(animationFrameRef.current);
        };
    }, [isPopupOpen, offers]);

    useEffect(() => {
        if (offers.length > 0) {
            const interval = setInterval(() => {
                setCurrentIndex((prevIndex) => (prevIndex + 1) % offers.length);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [offers]);

    // Creative Pill Navigation Styles
    const navLinkClass = ({ isActive }) =>
        isActive
            ? "relative underline underline-offset-4 text-black font-bold px-4 py-2 flex items-center transition-all duration-300 transform scale-105"
            : "relative text-neutral-900 font-semibold px-4 py-2 flex items-center hover:bg-white hover:text-red-700 transition-all duration-300 whitespace-nowrap group";

    const dropdownItemClass = ({ isActive }) =>
        isActive
            ? "block px-5 py-3 text-sm font-bold bg-red-50 text-red-700 transition-colors border-l-4 border-red-600"
            : "block px-5 py-3 text-sm font-semibold text-neutral-800 hover:bg-red-50 hover:text-red-700 hover:translate-x-1 transition-all duration-200 border-l-4 border-transparent";

    // Dropdown Animation Variants
    const dropdownVariants = {
        hidden: { opacity: 0, y: 15, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { type: "spring", stiffness: 200, damping: 20 },
        },
        exit: {
            opacity: 0,
            y: 10,
            scale: 0.95,
            transition: { duration: 0.15 },
        },
    };

    return (
        <>
            {/* Topbar - Dark background, concise with translation integrated */}
            <div className="bg-neutral-900 text-gray-300 text-[11px] md:text-xs py-1.5 px-4 md:px-8 flex flex-col md:flex-row justify-between items-center gap-1.5 md:gap-4 relative z-[101] w-full">
                {/* Left section: Contact & Mobile Translate */}
                <div className="flex items-center justify-between w-full md:w-auto shrink-0">
                    <div className="flex items-center space-x-2 font-medium lowercase tracking-wide">
                        <a
                            href="mailto:upscpyqpedia@gmail.com"
                            className="hover:text-yellow-400 transition-colors flex items-center gap-1"
                        >
                            <Mail size={12} />
                            <span className="hidden sm:inline">
                                upscpyqpedia@gmail.com
                            </span>
                            <span className="sm:hidden">Email Us</span>
                        </a>
                        <span className="text-gray-600">|</span>
                        <a
                            href="tel:+918709364253"
                            className="hover:text-yellow-400 transition-colors flex items-center gap-1"
                        >
                            <Phone size={12} />
                            +91 8709364253
                        </a>
                    </div>

                    {/* Mobile concise Translate Button */}
                    <button
                        className="md:hidden flex items-center gap-1 bg-white/10 active:bg-white/20 px-2.5 py-1 rounded-md text-white font-medium transition-colors"
                        onClick={() => {
                            setShowLangPopup(true);
                            setMobileMenuOpen(false);
                        }}
                    >
                        <Globe size={12} />
                        <span className="text-[10px] uppercase tracking-wider">
                            Language
                        </span>
                    </button>
                </div>

                {/* Center section: Marquee */}
                <div
                    className="w-full md:flex-1 h-6 flex items-center justify-center overflow-hidden"
                    ref={marqueeContainerRef}
                >
                    {loading ? (
                        <span className="text-gray-500 animate-pulse">
                            Checking latest updates...
                        </span>
                    ) : offers.length > 0 ? (
                        <div
                            className="whitespace-nowrap flex items-center h-full"
                            ref={marqueeTextRef}
                        >
                            <a
                                href={offers[currentIndex].link}
                                target="_blank"
                                rel="noreferrer"
                                className="text-white hover:text-yellow-400 font-semibold tracking-wide transition-colors"
                            >
                                {offers[currentIndex].title}
                            </a>
                        </div>
                    ) : null}
                </div>

                {/* Right section: Desktop Translate Button */}
                <button
                    className="hidden md:flex shrink-0 items-center gap-1.5 text-white transition-colors font-medium tracking-wide"
                    onClick={() => {
                        setShowLangPopup(true);
                    }}
                >
                    <Globe size={14} />
                    Change Language
                </button>
            </div>

            {/* Smart Sticky Main Navbar (Uses w-full to guarantee sticky lock) */}
            <nav
                className={`sticky top-0 z-[100] w-full px-4 md:px-8 flex items-center justify-between flex-wrap transition-all duration-500 ease-in-out ${
                    isScrolled
                        ? "bg-yellow-400/75 backdrop-blur-md shadow-xl py-2 border-b border-white/20"
                        : "bg-yellow-400 shadow-md py-4"
                }`}
            >
                {/* Logo */}
                <Link
                    to="/"
                    className="relative flex-shrink-0 cursor-pointer select-none group"
                >
                    <img
                        src="/assets/logo.png"
                        alt="Logo"
                        className={`object-contain transition-all duration-500 ${isScrolled ? "w-16 h-16" : "w-20 h-20"}`}
                    />
                    <div className="absolute inset-0 bg-white rounded-full scale-0 group-hover:scale-110 opacity-0 group-hover:opacity-10 -z-10 transition-all duration-500"></div>
                </Link>

                {/* Desktop Links */}
                <div className="hidden xl:flex items-center gap-2 flex-wrap">
                    <NavLink to="/" className={navLinkClass} end>
                        Home
                    </NavLink>

                    {/* About Dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={() => setOpenDropdown("about")}
                        onMouseLeave={() => setOpenDropdown(null)}
                    >
                        <div className="cursor-pointer text-neutral-900 font-semibold px-4 py-2 flex items-center hover:bg-white hover:text-red-700 transition-all duration-300 select-none">
                            <span>About</span>
                            <ChevronDown
                                size={14}
                                className={`ml-1.5 transition-transform duration-300 ${openDropdown === "about" ? "rotate-180 text-red-700" : ""}`}
                            />
                        </div>
                        <AnimatePresence>
                            {openDropdown === "about" && (
                                /* pt-4 creates the invisible hover bridge! */
                                <motion.div
                                    variants={dropdownVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="absolute top-full left-0 pt-4 w-52 z-50"
                                >
                                    <div className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] py-2 border border-gray-100 relative">
                                        <div className="absolute -top-2 left-6 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-100 z-0"></div>
                                        <div className="relative z-10">
                                            <NavLink
                                                to="/about"
                                                className={dropdownItemClass}
                                            >
                                                About Us
                                            </NavLink>
                                            <NavLink
                                                to="/selections"
                                                className={dropdownItemClass}
                                            >
                                                Selections
                                            </NavLink>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Courses Dropdown (Nested) */}
                    <div
                        className="relative"
                        onMouseEnter={() => setOpenDropdown("courses")}
                        onMouseLeave={() => setOpenDropdown(null)}
                    >
                        <div className="cursor-pointer text-neutral-900 font-semibold px-4 py-2 flex items-center hover:bg-white hover:text-red-700 transition-all duration-300 select-none">
                            <span>Courses</span>
                            <ChevronDown
                                size={14}
                                className={`ml-1.5 transition-transform duration-300 ${openDropdown === "courses" ? "rotate-180 text-red-700" : ""}`}
                            />
                        </div>
                        <AnimatePresence>
                            {openDropdown === "courses" && (
                                <motion.div
                                    variants={dropdownVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="absolute top-full left-0 pt-4 w-64 z-50"
                                >
                                    <div className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] py-2 border border-gray-100 relative">
                                        <div className="absolute -top-2 left-6 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-100 z-0"></div>
                                        <div className="relative z-10 flex flex-col">
                                            {pages
                                                .filter(
                                                    (p) =>
                                                        p &&
                                                        p.parent_id === null,
                                                )
                                                .map((pageObj) => {
                                                    const children =
                                                        pages.filter(
                                                            (p) =>
                                                                p.parent_id ===
                                                                pageObj.id,
                                                        );
                                                    return (
                                                        <div
                                                            key={pageObj.id}
                                                            className="relative group/sub"
                                                        >
                                                            <NavLink
                                                                to={`/course/course-page/${pageObj.id}`}
                                                                className="px-5 py-3 text-sm font-semibold text-neutral-800 hover:bg-red-50 hover:text-red-700 flex items-center justify-between w-full transition-all duration-200 border-l-4 border-transparent hover:border-red-600"
                                                            >
                                                                {pageObj.name}
                                                                {children.length >
                                                                    0 && (
                                                                    <ChevronRight
                                                                        size={
                                                                            14
                                                                        }
                                                                        className="text-gray-400 group-hover/sub:text-red-700 transition-colors"
                                                                    />
                                                                )}
                                                            </NavLink>

                                                            {/* Nested Submenu (pl-2 creates the horizontal bridge) */}
                                                            {children.length >
                                                                0 && (
                                                                <div className="absolute top-0 left-full pl-2 w-56 z-[60] invisible opacity-0 translate-x-4 group-hover/sub:visible group-hover/sub:opacity-100 group-hover/sub:translate-x-0 transition-all duration-300">
                                                                    <div className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] border border-gray-100 py-2 flex flex-col">
                                                                        {children.map(
                                                                            (
                                                                                child,
                                                                            ) => (
                                                                                <NavLink
                                                                                    key={
                                                                                        child.id
                                                                                    }
                                                                                    to={`/course/course-page/${child.id}`}
                                                                                    className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-700 hover:translate-x-1 transition-all duration-200"
                                                                                >
                                                                                    {
                                                                                        child.name
                                                                                    }
                                                                                </NavLink>
                                                                            ),
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <NavLink to="/test-series" className={navLinkClass}>
                        Test Series
                    </NavLink>
                    <NavLink to="/free-initiative" className={navLinkClass}>
                        Free Initiative
                    </NavLink>

                    {/* Vacancy Dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={() => setOpenDropdown("vacancy")}
                        onMouseLeave={() => setOpenDropdown(null)}
                    >
                        <div className="cursor-pointer text-neutral-900 font-semibold px-4 py-2 flex items-center hover:bg-white hover:text-red-700 transition-all duration-300 select-none">
                            <span>Recent Vacancies</span>
                            <ChevronDown
                                size={14}
                                className={`ml-1.5 transition-transform duration-300 ${openDropdown === "vacancy" ? "rotate-180 text-red-700" : ""}`}
                            />
                        </div>
                        <AnimatePresence>
                            {openDropdown === "vacancy" && (
                                <motion.div
                                    variants={dropdownVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="absolute top-full left-0 pt-4 w-52 z-50"
                                >
                                    <div className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] py-2 border border-gray-100 relative">
                                        <div className="absolute -top-2 left-6 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-100 z-0"></div>
                                        <div className="relative z-10">
                                            <NavLink
                                                to="/recent-vacancies/upsc"
                                                className={dropdownItemClass}
                                            >
                                                UPSC
                                            </NavLink>
                                            <NavLink
                                                to="/recent-vacancies/state-psc"
                                                className={dropdownItemClass}
                                            >
                                                State PSC
                                            </NavLink>
                                            <NavLink
                                                to="/recent-vacancies/other"
                                                className={dropdownItemClass}
                                            >
                                                Other
                                            </NavLink>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Resources Dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={() => setOpenDropdown("resources")}
                        onMouseLeave={() => setOpenDropdown(null)}
                    >
                        <div className="cursor-pointer text-neutral-900 font-semibold px-4 py-2 flex items-center hover:bg-white hover:text-red-700 transition-all duration-300 select-none">
                            <span>Resources</span>
                            <ChevronDown
                                size={14}
                                className={`ml-1.5 transition-transform duration-300 ${openDropdown === "resources" ? "rotate-180 text-red-700" : ""}`}
                            />
                        </div>
                        <AnimatePresence>
                            {openDropdown === "resources" && (
                                <motion.div
                                    variants={dropdownVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="absolute top-full left-0 pt-4 w-52 z-50"
                                >
                                    <div className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] py-2 border border-gray-100 relative">
                                        <div className="absolute -top-2 left-6 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-100 z-0"></div>
                                        <div className="relative z-10">
                                            <NavLink
                                                to="/downloads"
                                                className={dropdownItemClass}
                                            >
                                                Downloads
                                            </NavLink>
                                            <NavLink
                                                to="/current-affairs"
                                                className={dropdownItemClass}
                                            >
                                                Current Affairs
                                            </NavLink>
                                            <NavLink
                                                to="/blogs"
                                                className={dropdownItemClass}
                                            >
                                                Blogs
                                            </NavLink>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <NavLink to="/contact" className={navLinkClass}>
                        Contact Us
                    </NavLink>
                </div>

                {/* Profile & Tools Desktop */}
                <div
                    className="hidden xl:flex items-center space-x-3 relative"
                    ref={dropdownRef}
                >
                    <div
                        className="cursor-pointer select-none relative group"
                        onClick={() => setDropdownOpen(!isDropdownOpen)}
                    >
                        <div className="w-12 h-12 rounded-full bg-white text-yellow-600 shadow-md flex items-center justify-center font-extrabold text-xl border-2 border-transparent group-hover:border-yellow-400 group-hover:shadow-lg transition-all duration-300">
                            {user?.name ? (
                                user.name[0].toUpperCase()
                            ) : (
                                <User size={22} />
                            )}
                        </div>

                        <AnimatePresence>
                            {isDropdownOpen && (
                                <motion.div
                                    variants={dropdownVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="absolute top-full right-0 pt-4 w-56 z-[100]"
                                >
                                    <div className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] py-3 border border-gray-100 flex flex-col gap-1.5 px-3 relative">
                                        <div className="absolute -top-2 right-5 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-100 z-0"></div>
                                        <div className="relative z-10 flex flex-col">
                                            {isLoggedIn ? (
                                                <>
                                                    <div className="px-3 py-2 mb-2 border-b border-gray-100">
                                                        <p className="text-xs text-gray-500 font-medium">
                                                            Logged in as
                                                        </p>
                                                        <p className="text-sm font-bold text-neutral-900 truncate">
                                                            {user?.name ||
                                                                "Guest User"}
                                                        </p>
                                                    </div>
                                                    <Link
                                                        to="/dashboard"
                                                        className="bg-yellow-50 text-yellow-800 font-bold text-sm py-2.5 px-4 rounded-xl hover:bg-yellow-100 transition-colors flex items-center"
                                                    >
                                                        My Dashboard
                                                    </Link>
                                                    <Link
                                                        to="/change-password"
                                                        className="bg-gray-50 text-gray-700 font-bold text-sm py-2.5 px-4 rounded-xl hover:bg-gray-100 transition-colors flex items-center"
                                                    >
                                                        Security
                                                    </Link>
                                                    <button
                                                        onClick={handleLogout}
                                                        className="bg-red-50 text-red-700 font-bold text-sm py-2.5 px-4 rounded-xl hover:bg-red-100 transition-colors flex items-center mt-1 text-left"
                                                    >
                                                        Sign Out
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <Link
                                                        to="/login"
                                                        className="bg-neutral-900 text-white font-bold text-sm py-3 px-4 rounded-xl hover:bg-neutral-800 shadow-md hover:shadow-lg transition-all text-center"
                                                    >
                                                        Login to Account
                                                    </Link>
                                                    <Link
                                                        to="/register"
                                                        className="bg-yellow-50 text-yellow-800 font-bold text-sm py-3 px-4 rounded-xl hover:bg-yellow-100 transition-colors text-center mt-1"
                                                    >
                                                        Create Account
                                                    </Link>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Mobile Hamburger Menu Icon */}
                <button
                    className="xl:hidden flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-sm text-neutral-900 focus:outline-none"
                    onClick={toggleMobileMenu}
                >
                    <Menu size={24} />
                </button>
            </nav>

            {/* Premium Mobile Menu Drawer */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-neutral-900/40 backdrop-blur-sm z-[1000]"
                            onClick={toggleMobileMenu}
                        ></motion.div>

                        <motion.div
                            initial={{ x: "100%", opacity: 0.5 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: "100%", opacity: 0.5 }}
                            transition={{
                                type: "spring",
                                damping: 25,
                                stiffness: 200,
                            }}
                            className="fixed inset-y-0 right-0 w-full max-w-[320px] bg-white shadow-2xl z-[1001] flex flex-col overflow-hidden"
                        >
                            {/* Mobile Header */}
                            <div className="bg-neutral-900 p-6 flex flex-col items-center justify-center relative">
                                <button
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="absolute top-4 left-4 w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                                >
                                    <X size={18} />
                                </button>

                                <div className="w-20 h-20 rounded-full bg-white shadow-lg text-yellow-600 flex items-center justify-center font-extrabold text-3xl mb-4 border-4 border-neutral-800">
                                    {user?.name ? (
                                            user.name[0].toUpperCase()
                                    ) : (
                                        <User size={36} />
                                    )}
                                </div>
                                <h3 className="text-white font-bold text-lg">
                                        {user?.name || "Guest User"}
                                </h3>
                                <p className="text-gray-400 text-xs mt-1">
                                        {user?.email ||
                                        "Sign in to access courses"}
                                </p>
                                {isLoggedIn && (
                                    <Link
                                        to="/change-password"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="text-yellow-400 text-xs font-semibold mt-3 hover:text-yellow-300 transition-colors"
                                    >
                                        Security Settings
                                    </Link>
                                )}
                            </div>

                            {/* Mobile Links Scrollable Area */}
                            <div className="flex-1 overflow-y-auto px-4 py-6 space-y-2 custom-scrollbar bg-slate-50">
                                <NavLink
                                    to="/"
                                    className="w-full flex items-center py-3.5 px-4 bg-white rounded-xl text-neutral-900 font-bold shadow-sm"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Home
                                </NavLink>

                                {/* Accordion Items */}
                                {[
                                    {
                                        id: "m-about",
                                        label: "About",
                                        links: [
                                            { to: "/about", text: "About Us" },
                                            {
                                                to: "/selections",
                                                text: "Selections",
                                            },
                                        ],
                                    },
                                    {
                                        id: "m-vacancy",
                                        label: "Vacancies",
                                        links: [
                                            {
                                                to: "/recent-vacancies/upsc",
                                                text: "UPSC",
                                            },
                                            {
                                                to: "/recent-vacancies/state-psc",
                                                text: "State PSC",
                                            },
                                            {
                                                to: "/recent-vacancies/other",
                                                text: "Other",
                                            },
                                        ],
                                    },
                                    {
                                        id: "m-resources",
                                        label: "Resources",
                                        links: [
                                            {
                                                to: "/downloads",
                                                text: "Downloads",
                                            },
                                            {
                                                to: "/current-affairs",
                                                text: "Current Affairs",
                                            },
                                            { to: "/blogs", text: "Blogs" },
                                        ],
                                    },
                                ].map((menu) => (
                                    <div
                                        key={menu.id}
                                        className="bg-white rounded-xl shadow-sm overflow-hidden"
                                    >
                                        <button
                                            onClick={() =>
                                                toggleGroupedDropdown(menu.id)
                                            }
                                            className="w-full flex justify-between items-center py-3.5 px-4 text-neutral-900 font-bold"
                                        >
                                            {menu.label}
                                            <ChevronDown
                                                size={18}
                                                className={`text-red-600 transition-transform ${openDropdown === menu.id ? "rotate-180" : ""}`}
                                            />
                                        </button>
                                        <AnimatePresence>
                                            {openDropdown === menu.id && (
                                                <motion.div
                                                    initial={{
                                                        height: 0,
                                                        opacity: 0,
                                                    }}
                                                    animate={{
                                                        height: "auto",
                                                        opacity: 1,
                                                    }}
                                                    exit={{
                                                        height: 0,
                                                        opacity: 0,
                                                    }}
                                                    className="bg-slate-50 border-t border-gray-100"
                                                >
                                                    {menu.links.map((link) => (
                                                        <NavLink
                                                            key={link.to}
                                                            to={link.to}
                                                            className="block py-3 px-8 text-sm font-semibold text-gray-700 border-b border-gray-100 last:border-0"
                                                            onClick={() =>
                                                                setMobileMenuOpen(
                                                                    false,
                                                                )
                                                            }
                                                        >
                                                            {link.text}
                                                        </NavLink>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}

                                {/* Mobile Courses Mega Menu */}
                                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                                    <button
                                        onClick={() =>
                                            toggleGroupedDropdown("m-courses")
                                        }
                                        className="w-full flex justify-between items-center py-3.5 px-4 text-neutral-900 font-bold"
                                    >
                                        Courses
                                        <ChevronDown
                                            size={18}
                                            className={`text-red-600 transition-transform ${openDropdown === "m-courses" ? "rotate-180" : ""}`}
                                        />
                                    </button>
                                    <AnimatePresence>
                                        {openDropdown === "m-courses" && (
                                            <motion.div
                                                initial={{
                                                    height: 0,
                                                    opacity: 0,
                                                }}
                                                animate={{
                                                    height: "auto",
                                                    opacity: 1,
                                                }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className="bg-slate-50 border-t border-gray-100"
                                            >
                                                {pages
                                                    .filter(
                                                        (p) =>
                                                            p &&
                                                            p.parent_id ===
                                                                null,
                                                    )
                                                    .map((pageObj) => (
                                                        <div
                                                            key={pageObj.id}
                                                            className="border-b border-gray-200 last:border-0"
                                                        >
                                                            <NavLink
                                                                to={`/course/course-page/${pageObj.id}`}
                                                                className="block py-3 px-6 text-sm font-bold text-neutral-900 bg-gray-100"
                                                                onClick={() =>
                                                                    setMobileMenuOpen(
                                                                        false,
                                                                    )
                                                                }
                                                            >
                                                                {pageObj.name}
                                                            </NavLink>
                                                            <div className="flex flex-col bg-white">
                                                                {pages
                                                                    .filter(
                                                                        (p) =>
                                                                            p.parent_id ===
                                                                            pageObj.id,
                                                                    )
                                                                    .map(
                                                                        (
                                                                            child,
                                                                        ) => (
                                                                            <NavLink
                                                                                key={
                                                                                    child.id
                                                                                }
                                                                                to={`/course/course-page/${child.id}`}
                                                                                className="py-2.5 px-8 text-sm font-medium text-gray-600 border-b border-gray-50 last:border-0 hover:bg-red-50 hover:text-red-700"
                                                                                onClick={() =>
                                                                                    setMobileMenuOpen(
                                                                                        false,
                                                                                    )
                                                                                }
                                                                            >
                                                                                {
                                                                                    child.name
                                                                                }
                                                                            </NavLink>
                                                                        ),
                                                                    )}
                                                            </div>
                                                        </div>
                                                    ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                <NavLink
                                    to="/test-series"
                                    className="w-full flex items-center py-3.5 px-4 bg-white rounded-xl text-neutral-900 font-bold shadow-sm"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Test Series
                                </NavLink>
                                <NavLink
                                    to="/free-initiative"
                                    className="w-full flex items-center py-3.5 px-4 bg-white rounded-xl text-neutral-900 font-bold shadow-sm"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Free Initiative
                                </NavLink>
                                <NavLink
                                    to="/contact"
                                    className="w-full flex items-center py-3.5 px-4 bg-white rounded-xl text-neutral-900 font-bold shadow-sm"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Contact Us
                                </NavLink>

                                {isLoggedIn && (
                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center py-3.5 px-4 bg-red-50 rounded-xl text-red-600 font-bold shadow-sm border border-red-100"
                                    >
                                        Sign Out
                                    </button>
                                )}
                            </div>

                            {/* Mobile Footer Auth Buttons */}
                            <div className="p-4 bg-white border-t border-gray-100 shadow-[0_-10px_20px_rgba(0,0,0,0.03)] z-10">
                                {isLoggedIn ? (
                                    <Link
                                        to="/dashboard"
                                        onClick={() =>
                                            setMobileMenuOpen(false)
                                        }
                                        className="w-full block bg-neutral-900 text-white font-bold py-3.5 rounded-xl text-center shadow-md"
                                    >
                                        Dashboard
                                    </Link>
                                ) : (
                                    <div className="flex gap-3">
                                        <Link
                                            to="/login"
                                            onClick={() =>
                                                setMobileMenuOpen(false)
                                            }
                                            className="flex-1 bg-neutral-900 text-white font-bold py-3.5 rounded-xl text-center shadow-md"
                                        >
                                            Sign In
                                        </Link>
                                        <Link
                                            to="/register"
                                            onClick={() =>
                                                setMobileMenuOpen(false)
                                            }
                                            className="flex-1 bg-yellow-50 text-yellow-700 font-bold py-3.5 rounded-xl text-center shadow-sm border border-yellow-200"
                                        >
                                            Register
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {showLangPopup && (
                <LanguagePopup onClose={() => setShowLangPopup(false)} />
            )}
            <div style={{ display: "none" }}>
                <GoogleTranslate />
            </div>

            {/* Floating WhatsApp Action Button */}
            <div className="fixed bottom-6 right-6 z-[12000]">
                <a
                    href="https://wa.me/918709364253"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center w-14 h-14 bg-green-500 rounded-full shadow-2xl hover:-translate-y-1.5 transition-transform duration-300 relative group"
                >
                    <span className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75"></span>
                    <img
                        src="/assets/whatsapp-brands-solid-full.svg"
                        alt="WhatsApp"
                        className="w-7 h-7 relative z-10"
                        style={{ filter: "brightness(0) invert(1)" }}
                    />
                </a>
            </div>
        </>
    );
};

export default Navbar;
